// medplum-ai.js — the shared browser-side agent loop over Medplum's NATIVE `$ai`
// operation (POST fhir/R4/$ai), run AS THE LOGGED-IN USER (RLS the only lock).
//
// WHY THIS EXISTS ────────────────────────────────────────────────────────────
// Medplum's `$ai` operation is a thin, propose-only bridge to an OpenAI-compatible
// model: you POST { messages, model, tools } (all as JSON strings inside a FHIR
// Parameters resource) and it returns { content, tool_calls } — it NEVER executes
// the tool calls. The CALLER runs them. That is exactly the shape our hand-rolled
// server loop (server/opd/spaces.mjs) already had, so this lib replaces that loop:
// it drives the same multi-turn agent loop, but the model call goes through the
// user's own MedplumClient instead of a server-held OpenAI SDK key. The OpenAI key
// now lives ONLY as a Medplum project secret (OPENAI_API_KEY), never in our layer.
//
// GENERIC BY DESIGN ──────────────────────────────────────────────────────────
// This is intentionally decoupled from Spaces: `runAgentLoop` takes the model,
// system prompt, message list, tool schemas, and a caller-supplied
// `executeToolCall(name, args)` dispatcher. Spaces wires fhir_request (read-only)
// + propose_actions (staging) into that dispatcher; a later wave (the consult
// assistant) will reuse the SAME loop with its own tools. Keep the surface small
// and tool-agnostic.
//
// NO STREAMING this wave: `$ai` streaming does not support tool calls (Medplum
// docs), and we deliberately preserve today's request/response Spaces UX.
//
// FAIL LOUD: if `$ai` errors (feature off, key missing, OpenAI down, RLS forbids
// a tool call), the error surfaces to the caller — no Express fallback, no silent
// default. (AGENTS.md §1.3 / §2 redesign box.)
import { aiModel } from './ai-model.js';

// ── Parse `$ai`'s Parameters response into { content, toolCalls } ────────────
// Medplum returns a FHIR Parameters resource: parameter[name=content].valueString
// and parameter[name=tool_calls].valueString (a JSON string of the OpenAI
// tool_calls array). tool_calls is ABSENT when the model just answered.
function parseAiResponse(params) {
  const find = (n) => (params?.parameter || []).find((p) => p.name === n)?.valueString;
  const content = find('content') || '';
  let toolCalls = [];
  const raw = find('tool_calls');
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) toolCalls = parsed;
    } catch (_) {
      // A malformed tool_calls string means the model round-trip is unusable —
      // fail loud rather than silently drop the model's intended actions.
      throw new Error('$ai returned an unparseable tool_calls payload');
    }
  }
  return { content, toolCalls };
}

// Normalise ONE tool call to { id, name, args } regardless of whether `$ai`
// hands back arguments as an object (Medplum's docs show a parsed object) or as
// the raw OpenAI JSON string. Downstream code always gets a parsed args object.
function normaliseToolCall(call) {
  const fn = call?.function || {};
  let args = fn.arguments;
  if (typeof args === 'string') {
    try { args = JSON.parse(args || '{}'); } catch (_) { args = {}; }
  }
  if (!args || typeof args !== 'object') args = {};
  return { id: call?.id || `call_${Math.random().toString(36).slice(2, 10)}`, name: fn.name, args };
}

/**
 * Run the tool-calling agent loop against Medplum's `$ai` operation as the
 * logged-in user. Generic — no Spaces-specific logic lives here.
 *
 *   runAgentLoop({
 *     medplum,            // a logged-in @medplum/core MedplumClient (the USER's session)
 *     model,              // OPTIONAL explicit model id. Omit it (the normal case) and
 *                         // the loop resolves the deployment-config model via
 *                         // ai-model.js aiModel() — a model swap is then a config
 *                         // change, never a code change (owner decision D1).
 *     systemPrompt,       // string — prepended as the system message
 *     messages,           // [{ role:'user'|'assistant', content }] prior + current turn
 *     tools,              // OpenAI tool schema array (function tools)
 *     executeToolCall,    // async (name, args) => { toolResult, step?, staged?, blocks? }
 *                         //   toolResult: value fed back to the model (any JSON-able)
 *                         //   step:   optional UI/audit trail entry for THIS call
 *                         //   staged: optional array of staged (propose) actions to collect
 *                         //           (when present the result is NOT executed — it is deferred
 *                         //            to the caller's confirm UX; the loop still feeds `toolResult`
 *                         //            back so the model can wrap up)
 *                         //   blocks: optional array of UI render blocks to collect (data
 *                         //           visuals the caller renders under the reply — the loop
 *                         //           only accumulates them, it attaches no meaning)
 *                         //   choices: optional array of tappable options to collect (the
 *                         //           caller renders them; a tap sends a follow-up message)
 *     maxIterations = 10, // tool round-trip cap (matches the old server loop)
 *   })
 *   → { reply, steps, proposedActions, blocks, choices, model }
 *     (`model` is the id the loop actually ran — callers stamp it into their
 *      result metadata instead of re-resolving it)
 *
 * The loop:
 *   1. POST $ai with the running conversation.
 *   2. If no tool_calls → return the model's content as the reply.
 *   3. Otherwise dispatch each tool_call through executeToolCall, append the
 *      assistant tool-call message + each tool result to the conversation, and loop.
 *   4. On hitting maxIterations, do one final tool-free wrap-up $ai call.
 */
export async function runAgentLoop({
  medplum,
  model,
  systemPrompt,
  messages,
  tools,
  executeToolCall,
  maxIterations = 10,
}) {
  if (!medplum) throw new Error('runAgentLoop: a logged-in MedplumClient is required');
  if (typeof executeToolCall !== 'function') throw new Error('runAgentLoop: executeToolCall must be a function');

  // No explicit model → the deployment-config model (ai-model.js). This is THE
  // choke point every `$ai` agent passes through, so resolving here keeps the
  // model id out of the nine agent files.
  const resolvedModel = model || (await aiModel());

  const convo = [{ role: 'system', content: systemPrompt }, ...(messages || [])];
  const steps = [];
  const proposedActions = [];
  const blocks = [];
  const choices = [];

  // POST fhir/R4/$ai as the user → parsed { content, toolCalls }. Any HTTP/RLS
  // error propagates (fail loud). `extraParams` lets the wrap-up pass omit tools.
  const callAi = async (toolsForCall) => {
    const parameter = [
      { name: 'messages', valueString: JSON.stringify(convo) },
      { name: 'model', valueString: resolvedModel },
    ];
    if (toolsForCall && toolsForCall.length) {
      parameter.push({ name: 'tools', valueString: JSON.stringify(toolsForCall) });
    }
    const res = await medplum.post(medplum.fhirUrl('$ai'), { resourceType: 'Parameters', parameter });
    return parseAiResponse(res);
  };

  for (let i = 0; i < maxIterations; i++) {
    const { content, toolCalls } = await callAi(tools);

    if (!toolCalls.length) {
      return { reply: (content || '').trim() || 'Done.', steps, proposedActions, blocks, choices, model: resolvedModel };
    }

    // Record the assistant turn (with its tool_calls) so the follow-up tool
    // messages attach correctly — the OpenAI/`$ai` protocol requires the
    // assistant tool-call message to precede its tool results.
    //
    // CRITICAL: `$ai` hands tool_calls back with `function.arguments` as a PARSED
    // OBJECT, but when we echo the assistant message into the next request OpenAI
    // requires `arguments` to be a STRING (it rejects an object with a 400). So we
    // re-serialise arguments to a string on every echoed tool_call. (Verified live
    // against local Docker $ai — an object arguments field 400s the OpenAI call.)
    const echoedToolCalls = toolCalls.map((c) => {
      const fn = c?.function || {};
      const argStr = typeof fn.arguments === 'string' ? fn.arguments : JSON.stringify(fn.arguments ?? {});
      return { ...c, function: { ...fn, arguments: argStr } };
    });
    convo.push({ role: 'assistant', content: content || '', tool_calls: echoedToolCalls });

    for (const call of toolCalls) {
      const { id, name, args } = normaliseToolCall(call);
      let toolResult, step, staged, callBlocks, callChoices;
      try {
        const out = await executeToolCall(name, args);
        toolResult = out?.toolResult;
        step = out?.step;
        staged = out?.staged;
        callBlocks = out?.blocks;
        callChoices = out?.choices;
      } catch (e) {
        // A tool-dispatch failure is fed back to the model as an error tool
        // result (so it can recover or report), and recorded as a failed step.
        toolResult = { error: (e && e.message) || 'tool call failed' };
        step = { tool: name, ok: false, summary: (e && e.message) || 'tool call failed' };
      }
      if (step) steps.push(step);
      if (Array.isArray(staged) && staged.length) proposedActions.push(...staged);
      if (Array.isArray(callBlocks) && callBlocks.length) blocks.push(...callBlocks);
      if (Array.isArray(callChoices) && callChoices.length) choices.push(...callChoices);
      convo.push({
        role: 'tool',
        tool_call_id: id,
        content: typeof toolResult === 'string' ? toolResult : JSON.stringify(toolResult ?? {}),
      });
    }
  }

  // Hit the iteration cap — one final tool-free pass so the user still gets a
  // written answer instead of an empty turn.
  try {
    convo.push({ role: 'user', content: 'Summarise your findings now in a final answer — no more tool calls.' });
    const { content } = await callAi(null);
    return { reply: (content || '').trim() || 'I gathered the available data but reached the step limit.', steps, proposedActions, blocks, choices, model: resolvedModel };
  } catch (_) {
    return { reply: 'I reached the step limit while gathering the record. Please narrow the question.', steps, proposedActions, blocks, choices, model: resolvedModel };
  }
}
